import { Code, ConnectError } from "@connectrpc/connect";
import { skipToken, useQuery } from "@tanstack/react-query";

import { useEnvironmentOpsService } from "@/hooks/use-environment-ops-service";
import { keyWithPrincipal } from "@/queries/principal-key";

/**
 * Supervisor capabilities that can be probed. Each maps to a lightweight RPC
 * call that returns quickly and whose only purpose is to check whether the
 * supervisor version supports the feature.
 */
export type SupervisorCapability = "file-tree" | "terminal";

/**
 * Probes whether a specific supervisor RPC is available for the given
 * environment. Returns `available: false` when the RPC responds with
 * `Code.Unimplemented` (old supervisor), `true` on success, and
 * `undefined` while the probe is still in flight or retrying after
 * transient errors (network failures, timeouts).
 */
export function useSupervisorCapability(
    environmentId: string,
    capability: SupervisorCapability,
    { enabled = true }: { enabled?: boolean } = {},
): { available: boolean | undefined; isLoading: boolean } {
    const client = useEnvironmentOpsService(environmentId);

    const { data, isLoading } = useQuery({
        queryKey: keyWithPrincipal(["environments", "supervisor-capability", { environmentId, capability }]),
        queryFn: client
            ? async () => {
                  try {
                      switch (capability) {
                          case "file-tree":
                              await client.readFile({ path: ".", offset: 0n, length: 0n });
                              break;
                          case "terminal":
                              await client.listTerminals({});
                              break;
                      }
                      return true;
                  } catch (error) {
                      if (error instanceof ConnectError && error.code === Code.Unimplemented) {
                          return false;
                      }
                      // Transient errors (Unavailable, DeadlineExceeded, network) — re-throw
                      // so React Query retries instead of assuming the capability is available.
                      throw error;
                  }
              }
            : skipToken,
        enabled,
        staleTime: 1_000 * 60 * 5,
        gcTime: 1_000 * 60 * 10,
        retry: 3,
    });

    return { available: data, isLoading };
}
